import { useEffect, useState } from 'react';
import { groupChatAction } from '../../../lib/group-chat-api';
import { parseWhoCanAdd, type WhoCanAdd } from '../../../lib/group-chat-actions';
import { parseGroupPrivacy, type GroupPrivacy, type LastSeenSetting } from '../../../lib/group-receipts';
import { useGroupChatEnabled } from '../../../lib/group-chat-flag';
import { queryClient, queryKeys } from '../../../lib/query-client';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { SETTINGS_PAGE } from './settings-shared';

/**
 * Group chat R5 — "Who can add me to groups" (EVERYONE, the default / MY_CONTACTS / NOBODY), the caller's own setting
 * on symphony-bff (`my-settings` / `update-my-settings`). The same contract as iOS `SettingsScreen` and Android
 * `SettingsScreen.kt`: read when Settings opens (blank until then), a pick saves at once and shows optimistically, a
 * failed save goes back with the settings save error. Shown only when group chat is enabled for the person.
 *
 * Design §21 (2026-11-01): the same read carries the two privacy switches beside it — "Read receipts" (on by
 * default) and "Last seen & online" (Everyone / Nobody). Each change sends ONLY its own field, shows at once and
 * goes back on a failure; the open chats' receipts and members are then re-read (what the viewer may see changed).
 */
export function useGroupAddPolicy() {
  const enabled = useGroupChatEnabled();
  const { logPageClickBi } = useGenericBi();
  const [value, setValue] = useState<WhoCanAdd | null>(null);
  const [privacy, setPrivacy] = useState<GroupPrivacy | null>(null);
  const [saveFailed, setSaveFailed] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    let live = true;
    void groupChatAction('my-settings', {}).then((outcome) => {
      if (live && outcome.kind === 'ok') {
        setValue(parseWhoCanAdd(outcome.data));
        setPrivacy(parseGroupPrivacy(outcome.data));
      }
    });
    return () => {
      live = false;
    };
  }, [enabled]);

  const select = async (next: WhoCanAdd) => {
    const previous = value;
    logPageClickBi({ ...SETTINGS_PAGE, button_name: `group_add_${next.toLowerCase()}` });
    setValue(next);
    const outcome = await groupChatAction('update-my-settings', { whoCanAddMe: next });
    if (outcome.kind === 'ok') {
      setValue(parseWhoCanAdd(outcome.data));
      return;
    }
    setValue(previous);
    setSaveFailed(true);
  };

  const savePrivacy = async (change: Partial<GroupPrivacy>, button: string) => {
    const previous = privacy;
    logPageClickBi({ ...SETTINGS_PAGE, button_name: button });
    setPrivacy({ ...(previous ?? { readReceipts: true, lastSeen: 'EVERYONE' }), ...change });
    const outcome = await groupChatAction('update-my-settings', change);
    if (outcome.kind === 'ok') {
      setPrivacy(parseGroupPrivacy(outcome.data));
      // What the viewer may see changed: the member lists (presence) re-read now; a held window (receipts) is stale,
      // and the open chat's next read (its 30 s poll, or coming back to it) brings the new receipts.
      void queryClient.invalidateQueries({ queryKey: ['group-members'] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.chatWindows });
      return;
    }
    setPrivacy(previous);
    setSaveFailed(true);
  };

  return {
    enabled,
    value,
    select,
    privacy,
    setReadReceipts: (on: boolean) => savePrivacy({ readReceipts: on }, on ? 'group_read_receipts_on' : 'group_read_receipts_off'),
    setLastSeen: (next: LastSeenSetting) => savePrivacy({ lastSeen: next }, `group_last_seen_${next.toLowerCase()}`),
    saveFailed,
    dismissSaveFailed: () => setSaveFailed(false),
  };
}
